/* Copyright (c) 2021-2023 Nordcloud Oy or its affiliates. All Rights Reserved. */

import { useMemo } from "react";
import { useTopSpendingApplicationsQuery } from "~/generated/graphql";
import { useCurrency } from "~/hooks";
import { applyRate } from "~/tools";
import { formatTotalCosts } from "./helpers";

export function useTopSpendingApplications() {
  const { currency, baseCurrency } = useCurrency();
  const { data, loading, error } = useTopSpendingApplicationsQuery({
    variables: { limit: 6 },
  });

  const topSpendingApps = useMemo(
    () => data?.topSpendingApplications.topSpending ?? [],
    [data]
  );

  const showOthers = topSpendingApps.length > 5;
  const topSpendingLimit = topSpendingApps.length < 6 ? 5 : 4;

  const memoizedData = useMemo(() => {
    const allApps = data?.topSpendingApplications
      ? [
          ...topSpendingApps.slice(0, topSpendingLimit),
          ...(showOthers ? [data.topSpendingApplications.other] : []),
          ...(data.topSpendingApplications.defaultApplication
            ? [data.topSpendingApplications.defaultApplication]
            : []),
        ]
      : [];

    const appSpendsByNameValue = allApps.map((app) => ({
      name: app.name,
      value: parseFloat(app.currentMonth || ""),
    }));

    return {
      allApps,
      appSpendsByNameValue,
      totalCosts: parseFloat(
        data?.topSpendingApplications.total.currentMonth ?? "0"
      ),
    };
  }, [data, showOthers, topSpendingApps, topSpendingLimit]);

  const areCostsAvailable = memoizedData.totalCosts > 0;

  const topSpendingApplicationSum = formatTotalCosts(
    applyRate(memoizedData.totalCosts, currency, baseCurrency),
    currency
  );

  return {
    data: memoizedData,
    topSpendingApplicationSum,
    areCostsAvailable,
    loading,
    error,
  };
}
